import React, { useCallback, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import type { FrontOverlayMode } from '../../../ips-react-front-fw/components/overlay';
import { MembershipOverlay } from './MembershipOverlay';
import type { MembershipOverlayMode } from './types';
import { registerDynamicBootstrap } from '../../shared/registerDynamicBootstrap';
import { useMembersAreaPagination } from './useMembersAreaPagination';
import './membership.css';

const overlayMountSelector = '#ips-react-membership-root';
const runtimeMountId = 'ips-react-membership-runtime-root';
const sectionSelector = '.ips-membership-section-content';
const cardLinkSelector = 'a.ips_solution_cards_linkset[href]';

const isOverlayMode = (value: string | undefined): value is FrontOverlayMode => value === 'drawer' || value === 'modal';

const normalizeMode = (value: string | undefined): MembershipOverlayMode => {
  if (value === 'page') {
    return 'page';
  }

  return isOverlayMode(value) ? value : 'page';
};

const withEmbedFlag = (href: string): string => {
  try {
    const url = new URL(href, window.location.origin);
    if (url.origin === window.location.origin) {
      url.searchParams.set('ips_membership_embed', '1');
    }
    return url.toString();
  } catch (error) {
    return href;
  }
};

const bootstrap = () => {
  if (new URL(window.location.href).searchParams.get('ips_membership_embed') === '1') {
    return;
  }

  const overlayMount = document.querySelector<HTMLElement>(overlayMountSelector);
  const hasMembershipSection = Boolean(document.querySelector(sectionSelector));
  if (!overlayMount && !hasMembershipSection) {
    return;
  }

  let mount = overlayMount;

  if (!mount) {
    mount = document.getElementById(runtimeMountId) as HTMLElement | null;
    if (!mount) {
      mount = document.createElement('div');
      mount.id = runtimeMountId;
      mount.style.display = 'none';
      document.body.appendChild(mount);
    }
  }

  if (!mount || mount.dataset.ipsReactMounted === '1') {
    return;
  }

  mount.dataset.ipsReactMounted = '1';

  const mode = normalizeMode(overlayMount?.dataset.viewMode);
  const root = createRoot(mount);

  const App: React.FC = () => {
    useMembersAreaPagination();

    const [open, setOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [iframeSrc, setIframeSrc] = useState<string>('');
    const [openPageHref, setOpenPageHref] = useState<string>('');

    const closeOverlay = useCallback(() => {
      setOpen(false);
      setLoading(false);
    }, []);

    const openOverlay = useCallback((href: string) => {
      setOpenPageHref(href);
      setIframeSrc(withEmbedFlag(href));
      setLoading(true);
      setOpen(true);
    }, []);

    useEffect(() => {
      const handleClick = (event: Event) => {
        const anchor = (event.target as HTMLElement)?.closest<HTMLAnchorElement>(cardLinkSelector);
        if (!anchor) {
          return;
        }

        const withinMembershipContent = anchor.closest('.wooorder-membership-content');
        if (!withinMembershipContent) {
          return;
        }

        const href = anchor.getAttribute('href') || '';
        if (!href || href.trim() === '' || href.trim() === '#') {
          return;
        }

        if (mode === 'page') {
          return;
        }

        event.preventDefault();
        openOverlay(anchor.href);
      };

      document.addEventListener('click', handleClick, true);
      return () => document.removeEventListener('click', handleClick, true);
    }, [openOverlay]);

    if (mode === 'page') {
      return null;
    }

    return (
      <MembershipOverlay
        open={open}
        mode={mode}
        loading={loading}
        iframeSrc={iframeSrc}
        openPageHref={openPageHref}
        onClose={closeOverlay}
        onIframeLoad={() => setLoading(false)}
      />
    );
  };

  root.render(<App />);
};

registerDynamicBootstrap(bootstrap);
